import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import InputAdornment from "@material-ui/core/InputAdornment";

import GavelIcon from "@material-ui/icons/Gavel";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import CheckCircleIcon from "@material-ui/icons/CheckCircleOutline";
import CancelIcon from "@material-ui/icons/HighlightOff";
import DeleteIcon from "@material-ui/icons/Delete";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import useConfirm from "../../hooks/useConfirm";
import useSocketSubscription from "../../hooks/useSocketSubscription";
import { displayClientName } from "../../utils/clientName";

const STATUS_META = {
  draft: { label: "Rascunho", tone: "neutral" },
  active: { label: "Vigente", tone: "success" },
  expiring: { label: "Vencendo", tone: "warning" },
  expired: { label: "Vencido", tone: "danger" },
  renewed: { label: "Renovado", tone: "info" },
  terminated: { label: "Rescindido", tone: "neutral" },
};

const fmtMoney = (v, currency = "BRL") =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency });

const fmtDate = (d) =>
  d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "—";

/** Dias até a data, em relação a hoje. Negativo = já passou. */
const daysUntil = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  const target = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86400000);
};

const useStyles = makeStyles((theme) => ({
  number: {
    fontFamily: "'SF Mono', 'Roboto Mono', Menlo, monospace",
    fontSize: 12,
    fontWeight: 600,
  },
  money: { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
  date: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  soon: {
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    color: theme.palette.warning.main,
    fontWeight: 600,
  },
  late: {
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  muted: { color: theme.palette.text.hint },
  sub: { color: theme.palette.text.secondary, display: "block" },
  note: { marginTop: theme.spacing(1), color: theme.palette.text.secondary, fontSize: 13 },
}));

const Contracts = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", search: "" });
  const [renewal, setRenewal] = useState({
    open: false,
    contract: null,
    months: 12,
    adjustmentPct: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.search) params.search = filter.search;
      const { data } = await api.get("/contracts", { params });
      setRows(data?.contracts || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  // O worker muda status sozinho (vencendo, vencido, renovado); sem isto a
  // lista aberta na tela mostraria um estado que já não é o do banco.
  useSocketSubscription("contract", load);

  const activate = async (c) => {
    const ok = await confirm({
      title: `Ativar o contrato ${c.number}?`,
      message:
        "O contrato passa a valer e entra na vigilância de vencimento. A data de assinatura é registrada agora.",
      confirmLabel: "Ativar",
      destructive: false,
    });
    if (!ok) return;
    try {
      await api.patch(`/contracts/${c.id}/status`, { status: "active" });
      toast.success("Contrato ativado.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const terminate = async (c) => {
    const ok = await confirm({
      title: `Rescindir o contrato ${c.number}?`,
      message: "O contrato deixa de valer e sai da vigilância de vencimento.",
      confirmLabel: "Rescindir",
    });
    if (!ok) return;
    try {
      await api.patch(`/contracts/${c.id}/status`, {
        status: "terminated",
        reason: "Rescindido pela equipe",
      });
      toast.success("Contrato rescindido.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const remove = async (c) => {
    const ok = await confirm({
      title: `Excluir o contrato ${c.number}?`,
      message: "Só rascunhos podem ser excluídos. Contrato que já valeu se rescinde.",
    });
    if (!ok) return;
    try {
      await api.delete(`/contracts/${c.id}`);
      toast.success("Contrato excluído.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const submitRenewal = async () => {
    try {
      const { data } = await api.post(`/contracts/${renewal.contract.id}/renew`, {
        months: Number(renewal.months) || 12,
        adjustmentPct: Number(renewal.adjustmentPct) || 0,
      });
      toast.success(`Renovado: ${data.number}`);
      setRenewal({ open: false, contract: null, months: 12, adjustmentPct: 0 });
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const columns = [
    {
      key: "number",
      label: "Número",
      render: (c) => <span className={classes.number}>{c.number}</span>,
    },
    {
      key: "title",
      label: "Contrato",
      render: (c) => (
        <div>
          <Typography variant="subtitle2" noWrap>{c.title}</Typography>
          <Typography variant="caption" className={classes.sub}>
            {displayClientName(c.client, `Cliente #${c.clientId}`)}
          </Typography>
        </div>
      ),
    },
    {
      key: "status",
      label: "Situação",
      render: (c) => {
        const meta = STATUS_META[c.status] || { label: c.status, tone: "neutral" };
        return <StatusChip dot tone={meta.tone} label={meta.label} />;
      },
    },
    {
      key: "endDate",
      label: "Vigência até",
      render: (c) => {
        if (!c.endDate) {
          return <span className={classes.muted}>indeterminado</span>;
        }
        const left = daysUntil(c.endDate);
        const vivo = ["active", "expiring"].includes(c.status);
        // Quantos dias faltam é o dado que faz alguém agir. A data sozinha
        // obriga a fazer a conta de cabeça, linha a linha.
        const cls = !vivo
          ? classes.date
          : left < 0
          ? classes.late
          : left <= Number(c.noticeDays || 30)
          ? classes.soon
          : classes.date;
        const sufixo = vivo
          ? left < 0
            ? ` (${Math.abs(left)}d atrás)`
            : ` (${left}d)`
          : "";
        return <span className={cls}>{fmtDate(c.endDate)}{sufixo}</span>;
      },
    },
    {
      key: "value",
      label: "Valor",
      align: "right",
      render: (c) => (
        <span className={classes.money}>{fmtMoney(c.value, c.currency)}</span>
      ),
    },
    {
      key: "autoRenew",
      label: "Renovação",
      render: (c) =>
        c.autoRenew ? (
          <StatusChip tone="info" label={`Automática · ${c.renewalMonths}m`} />
        ) : (
          <span className={classes.muted}>manual</span>
        ),
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: (c) => (
        <>
          {c.status === "draft" && (
            <Tooltip title="Ativar">
              <IconButton size="small" onClick={() => activate(c)}>
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {["active", "expiring", "expired"].includes(c.status) && (
            <Tooltip title="Renovar">
              <IconButton
                size="small"
                onClick={() =>
                  setRenewal({
                    open: true,
                    contract: c,
                    months: c.renewalMonths || 12,
                    adjustmentPct: Number(c.adjustmentPct) || 0,
                  })
                }
              >
                <AutorenewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {["active", "expiring"].includes(c.status) && (
            <Tooltip title="Rescindir">
              <IconButton size="small" onClick={() => terminate(c)}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {c.status === "draft" && (
            <Tooltip title="Excluir">
              <IconButton size="small" onClick={() => remove(c)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const novoValor =
    renewal.contract &&
    Number(renewal.contract.value) * (1 + (Number(renewal.adjustmentPct) || 0) / 100);

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Contratos"
        count={rows.length}
        subtitle="Vigência, renovação e reajuste. O sistema avisa antes do fim e renova sozinho o que estiver marcado."
        filters={
          <>
            <TextField
              label="Buscar"
              value={filter.search}
              onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
            />
            <TextField
              select
              label="Situação"
              style={{ minWidth: 180 }}
              value={filter.status}
              onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(STATUS_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>
                  {meta.label}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyIcon={<GavelIcon />}
        emptyTitle="Nenhum contrato"
        emptyDescription="Contratos guardam vigência e condição de renovação. Quem tem contrato cadastrado é avisado antes de ele vencer, em vez de descobrir depois."
      />

      <Dialog
        open={renewal.open}
        onClose={() => setRenewal({ open: false, contract: null, months: 12, adjustmentPct: 0 })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Renovar {renewal.contract?.number}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A nova vigência começa no dia seguinte ao fim da atual
            {renewal.contract?.endDate ? ` (${fmtDate(renewal.contract.endDate)})` : ""}.
            O contrato atual vira histórico.
          </DialogContentText>
          <TextField
            label="Meses de vigência"
            type="number"
            fullWidth
            margin="dense"
            value={renewal.months}
            onChange={(e) => setRenewal((r) => ({ ...r, months: e.target.value }))}
          />
          <TextField
            label="Reajuste"
            type="number"
            fullWidth
            margin="dense"
            value={renewal.adjustmentPct}
            onChange={(e) => setRenewal((r) => ({ ...r, adjustmentPct: e.target.value }))}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText={renewal.contract?.adjustmentIndex || "Informe o percentual efetivo"}
          />
          {renewal.contract && (
            <div className={classes.note}>
              {fmtMoney(renewal.contract.value, renewal.contract.currency)} →{" "}
              <strong>{fmtMoney(novoValor, renewal.contract.currency)}</strong>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRenewal({ open: false, contract: null, months: 12, adjustmentPct: 0 })}
          >
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={submitRenewal}>
            Renovar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Contracts;
